import PageTitle from '../PageTitle';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../common/FormInput';
import { ProductType, SelectOpt } from '../../common/CommonTypes';
import { useContext, useEffect, useRef, useState } from 'react';
import { ClaimContext } from 'context/ClaimContext';
import Vehicle from 'components/sitePages/vehicle/Vehicle';
import DateInput from '../dateInput/DateInput';
import { useTranslation } from 'react-i18next';
import SelectInput, { SelectInputHelper } from '../selectInput/SelectInput';
import { CommonValidation } from '../commonValidation/commonValidation';
import { RoutePaths } from 'Constants/Common';
import LocationCountry from '../LocationCountry/LocationCountry';
import ErrorSummary from '../ErrorSummary/ErrorSummary';
import { useAppSelector } from 'state/hooks';
export type LocationState = {
    product: ProductType;
};

export type GapClaimErrors = {
    dateOfIncident?: string;
    outstandingLoanBalance?: string;
    vehicleStolen?: string;
    locationCountry?: string;
    fullCoverage?: string;
    settlementReceived?: string;
    settlementReceivedDate?: string;
    settlementPriorToIncident?: string;
};

export const SelectOptionsYesNo = {
    Yes: 'Yes',
    No: 'No'
} as const;

const ClaimDetailsPage = (): JSX.Element => {
    const scrollDivRef = useRef<null | HTMLDivElement>(null);
    useEffect(() => {
        scrollDivRef.current?.scrollIntoView();
    }, []);

    const { t } = useTranslation();
    const navigate = useNavigate();

    const YesNoOptions: SelectOpt[] = [
        {
            id: SelectOptionsYesNo.Yes,
            text: t('option_yes'),
            value: SelectOptionsYesNo.Yes,
            disabled: false
        },
        {
            id: SelectOptionsYesNo.No,
            text: t('option_no'),
            value: SelectOptionsYesNo.No,
            disabled: false
        }
    ];

    const [formErrors, setFormError] = useState<GapClaimErrors>({});
    const vehicles = useAppSelector((s) => s.vehicles.vehicleList);
    const { productReference, gapClaim, setGapClaim, setGapProductReference } =
        useContext(ClaimContext);
    const selectedProductReference =
        useAppSelector((s) => s.vehicles.selectedPolicy?.reference) ??
        productReference?.reference ??
        '';
    const selectedProduct = useAppSelector((s) => s.vehicles.selectedPolicy);

    useEffect(() => {
        if (selectedProduct) {
            setGapProductReference(selectedProduct);
        }
    }, []);

    const vehicle = vehicles.find((p) =>
        p.policies.some((r) => r.reference === selectedProductReference)
    );

    const errorlist = (): Array<string | undefined> => {
        const values = [
            formErrors?.dateOfIncident,
            formErrors?.outstandingLoanBalance,
            formErrors?.locationCountry,
            formErrors?.vehicleStolen,
            formErrors?.fullCoverage,
            formErrors?.settlementReceived,
            formErrors?.settlementReceivedDate
        ];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };

    useEffect(() => {
        if (gapClaim.settlementReceived === SelectOptionsYesNo.Yes) {
            // When settlement received changes to "Yes", treat settlement as the changed field
            validateDateComparison(
                gapClaim.dateOfIncident,
                gapClaim.settlementReceivedDate,
                'settlement'
            );
        } else if (gapClaim.settlementReceived === SelectOptionsYesNo.No) {
            // When settlement received changes to "No", clear any date comparison errors
            const errors: GapClaimErrors = { ...formErrors };
            delete errors.dateOfIncident;
            delete errors.settlementReceivedDate;
            setFormError(errors);
            setLastChangedField(null);
        }
    }, [gapClaim.settlementReceived]); // Run when settlement received status changes

    const onChangDateOfIncident = (selectedIncidentDate: string) => {
        setGapClaim({
            ...gapClaim,
            dateOfIncident: selectedIncidentDate
        });

        validateDateComparison(
            selectedIncidentDate,
            gapClaim.settlementReceivedDate,
            'incident'
        );
    };

    const onChangSettlementReceivedDate = (selectedDate: string) => {
        setGapClaim({
            ...gapClaim,
            settlementReceivedDate: selectedDate
        });

        validateDateComparison(
            gapClaim.dateOfIncident,
            selectedDate,
            'settlement'
        );
    };

    const onChangeOutstandingBalance = (selectedValue: string) => {
        setGapClaim({
            ...gapClaim,
            outstandingLoanBalance: selectedValue
        });
    };

    const onChangeVehicleStolen = (selectedValue: string) => {
        setGapClaim({
            ...gapClaim,
            vehicleStolen: selectedValue
        });
    };
    const onChangeFullCoverage = (selectedValue: string) => {
        setGapClaim({
            ...gapClaim,
            fullCoverage: selectedValue,
            settlementReceived:
                selectedValue == SelectOptionsYesNo.Yes
                    ? gapClaim.settlementReceived
                    : SelectInputHelper.NullValue()
        });
    };

    const onChangeSettlementReceived = (selectedValue: string) => {
        const updatedClaim = {
            ...gapClaim,
            settlementReceived: selectedValue,
            settlementReceivedDate: null
        };

        setGapClaim(updatedClaim);

        // Clear settlement date error when switching to "No"
        if (selectedValue === SelectOptionsYesNo.No) {
            const errors: GapClaimErrors = { ...formErrors };
            delete errors.settlementReceivedDate;
            delete errors.dateOfIncident;
            setFormError(errors);
            setLastChangedField(null);
        } else if (selectedValue === SelectOptionsYesNo.Yes) {
            // If switching to "Yes", validate existing dates
            // Since we're enabling settlement date, treat it as the changed field
            validateDateComparison(gapClaim.dateOfIncident, null, 'settlement');
        }
    };

    const isDateOfIncidentInValid =
        gapClaim.vehicleStolen === SelectOptionsYesNo.Yes &&
        CommonValidation.daysPassed(gapClaim.dateOfIncident) < 30;

    const onChangeLocationCountry = (selectedValue: string): void => {
        const updatedClaim = {
            ...gapClaim,
            locationCountry: selectedValue
        };

        setGapClaim(updatedClaim);

        // Clear the location country error when a value is selected
        if (formErrors.locationCountry) {
            const errors = { ...formErrors };
            delete errors.locationCountry;
            setFormError(errors);
        }
    };

    const onContinueClaim = (): void => {
        if (isPageValid()) navigate(RoutePaths.ClaimSummary);
    };

    const onCancelClicked = (): void => {
        navigate(RoutePaths.Home);
    };

    const isPageValid = (): boolean => {
        let validationError = false;
        const errors: GapClaimErrors = {};

        if (CommonValidation.isDateNull(gapClaim.dateOfIncident, true)) {
            errors.dateOfIncident = t(
                'gap_claim_date_of_incident_required_error'
            );
            validationError = true;
        }

        if (!CommonValidation.isValidDate(gapClaim.dateOfIncident, true)) {
            errors.dateOfIncident = t(
                'gap_claim_date_of_incident_invalid_error'
            );
            validationError = true;
        }

        if (!SelectInputHelper.isValidValue(gapClaim.locationCountry)) {
            errors.locationCountry = t('gap_claim_country_required_error');
            validationError = true;
        }

        if (!SelectInputHelper.isValidValue(gapClaim.outstandingLoanBalance)) {
            errors.outstandingLoanBalance = t(
                'gap_claim_outstanding_loan_balance_required_error'
            );
            validationError = true;
        } else if (
            gapClaim.outstandingLoanBalance == SelectOptionsYesNo.Yes &&
            !SelectInputHelper.isValidValue(gapClaim.vehicleStolen)
        ) {
            errors.vehicleStolen = t('gap_claim_stolen_vehicle_required_error');
            validationError = true;
        }

        if (!SelectInputHelper.isValidValue(gapClaim.fullCoverage)) {
            errors.fullCoverage = t('gap_claim_insurance_required_error');
            validationError = true;
        } else if (
            gapClaim.fullCoverage == SelectOptionsYesNo.Yes &&
            !SelectInputHelper.isValidValue(gapClaim.settlementReceived)
        ) {
            errors.settlementReceived = t(
                'gap_claim_settlement_received_required_error'
            );
            validationError = true;
        } else if (
            gapClaim.settlementReceived == SelectOptionsYesNo.Yes &&
            CommonValidation.isDateNull(gapClaim.settlementReceivedDate, true)
        ) {
            errors.settlementReceived = t(
                'gap_claim_settlement_received_date_required_error'
            );
            validationError = true;
        } else if (
            !CommonValidation.isValidDate(gapClaim.settlementReceivedDate, true)
        ) {
            errors.settlementReceivedDate = t(
                'gap_claim_settlement_received_date_invalid_error'
            );
            validationError = true;
        }

        if (
            gapClaim.settlementReceived == SelectOptionsYesNo.Yes &&
            !CommonValidation.isDateNull(
                gapClaim.settlementReceivedDate,
                true
            ) &&
            !CommonValidation.isDateNull(gapClaim.dateOfIncident, true) &&
            CommonValidation.isValidDate(
                gapClaim.settlementReceivedDate,
                true
            ) &&
            CommonValidation.isValidDate(gapClaim.dateOfIncident, true)
        ) {
            const dateDifference = CommonValidation.dateDifference(
                gapClaim.settlementReceivedDate,
                gapClaim.dateOfIncident
            );

            if (dateDifference < 0) {
                // Use the last changed field to determine where to show the error
                if (lastChangedField === 'incident') {
                    errors.dateOfIncident = t(
                        'gap_claim_settlement_before_incident_error'
                    );
                } else {
                    errors.settlementReceivedDate = t(
                        'gap_claim_settlement_before_incident_error'
                    );
                }
                validationError = true;
            }
        }

        setFormError(errors);

        return !validationError;
    };

    const [lastChangedField, setLastChangedField] = useState<
        'incident' | 'settlement' | null
    >(null);

    const validateDateComparison = (
        incidentDate: string | null,
        settlementDate: string | null,
        changedField: 'incident' | 'settlement'
    ) => {
        const errors: GapClaimErrors = { ...formErrors };

        delete errors.dateOfIncident;
        delete errors.settlementReceivedDate;

        if (
            gapClaim.settlementReceived == SelectOptionsYesNo.Yes &&
            !CommonValidation.isDateNull(settlementDate, true) &&
            !CommonValidation.isDateNull(incidentDate, true) &&
            CommonValidation.isValidDate(settlementDate, true) &&
            CommonValidation.isValidDate(incidentDate, true)
        ) {
            const dateDifference = CommonValidation.dateDifference(
                settlementDate,
                incidentDate
            );

            if (dateDifference < 0) {
                // Show error on the field that was just changed
                if (changedField === 'incident') {
                    errors.dateOfIncident = t(
                        'gap_claim_settlement_before_incident_error'
                    );
                } else {
                    errors.settlementReceivedDate = t(
                        'gap_claim_settlement_before_incident_error'
                    );
                }
            }
        }

        setFormError(errors);
    };

    const enableContinueButton = (): boolean => {
        if (
            gapClaim.fullCoverage == SelectOptionsYesNo.No ||
            gapClaim.settlementReceived == SelectOptionsYesNo.No
        )
            return true;

        if (
            gapClaim.fullCoverage == SelectOptionsYesNo.Yes &&
            !CommonValidation.isDateNull(gapClaim.dateOfIncident, true) &&
            !CommonValidation.isDateNull(
                gapClaim.settlementReceivedDate,
                true
            ) &&
            CommonValidation.isValidDate(gapClaim.dateOfIncident, true) &&
            CommonValidation.isValidDate(gapClaim.settlementReceivedDate, true)
        ) {
            const dateDifference = CommonValidation.dateDifference(
                gapClaim.settlementReceivedDate,
                gapClaim.dateOfIncident
            );

            // Check that settlement date is not prior to incident date and within 90 days
            if (dateDifference >= 0 && dateDifference <= 90) {
                return true;
            }
        }

        // Default is false
        return false;
    };

    const maxDate = () => {
        const dt = new Date();
        dt.setDate(dt.getDate());
        return dt;
    };

    return (
        <div>
            <div ref={scrollDivRef}></div>
            <div className='card page-content'>
                <PageTitle title={t('claim_details_title')} />
                <div className='card-body page-content-body'>
                    <FormInput
                        id='summary-vehicle-forminput'
                        label={t('vehicle_details')}>
                        <div
                            id='vehicle-summary-container'
                            className='div-container-style'>
                            <section>
                                {vehicle && (
                                    <Vehicle
                                        id={'vehicle-${vehicle.vrn}'}
                                        vehicle={vehicle}
                                    />
                                )}
                            </section>
                        </div>
                    </FormInput>
                </div>
                <div className='card-body page-content-body' id='date-section'>
                    <FormInput
                        id='incident-date-forminput'
                        errorText={formErrors?.dateOfIncident}
                        label={t('incident_date')}>
                        <DateInput
                            name='dateOfIncidentDatePicker'
                            useJson
                            forceTimeReset
                            value={gapClaim.dateOfIncident}
                            onDateChanged={(dateString) =>
                                onChangDateOfIncident(dateString)
                            }
                            maximumDate={maxDate()}
                        />
                    </FormInput>
                    <FormInput
                        id='outstanding-loan-balance-forminput'
                        label={t('outstanding_loan_balance_question')}
                        errorText={formErrors?.outstandingLoanBalance}>
                        <SelectInput
                            name='outstanding-loan-balance'
                            loadingText={t('loading')}
                            value={gapClaim.outstandingLoanBalance}
                            options={YesNoOptions}
                            onChange={onChangeOutstandingBalance}
                            forceDefault
                            defaultIfOneOption
                            addDefaultOption
                            defaultText={t('select_default')}
                        />
                        {gapClaim.outstandingLoanBalance ==
                            SelectOptionsYesNo.No && (
                            <div
                                className='alert alert-warning panel-selection-alert-container'
                                role='alert'>
                                {t('claim_not_covered')}
                            </div>
                        )}
                    </FormInput>
                    {gapClaim.outstandingLoanBalance ==
                        SelectOptionsYesNo.Yes && (
                        <FormInput
                            id='vehicle-stolen-forminput'
                            label={t('vehicle_stolen_question')}
                            errorText={formErrors?.vehicleStolen}>
                            <SelectInput
                                name='vehicle-stolen'
                                loadingText={t('loading')}
                                value={gapClaim.vehicleStolen}
                                options={YesNoOptions}
                                onChange={onChangeVehicleStolen}
                                forceDefault
                                defaultIfOneOption
                                addDefaultOption
                                defaultText={t('select_default')}
                            />
                            {isDateOfIncidentInValid && (
                                <div
                                    className='alert alert-warning panel-selection-alert-container'
                                    role='alert'>
                                    {t('submit_claim_after_30_days')}
                                </div>
                            )}
                        </FormInput>
                    )}
                    <FormInput
                        id='location-country-forminput'
                        label={t('location_country_question')}
                        errorText={formErrors?.locationCountry}>
                        <LocationCountry
                            locationCountry={gapClaim.locationCountry}
                            onChangeLocationCountry={onChangeLocationCountry}
                        />
                        {gapClaim.locationCountry == 'Other' && (
                            <div
                                className='alert alert-warning panel-selection-alert-container'
                                role='alert'>
                                {t('claim_not_covered')}
                            </div>
                        )}
                    </FormInput>
                    <FormInput
                        id='claim-insurace-forminput'
                        label={t('vehicle_full_coverage_question')}
                        errorText={formErrors?.fullCoverage}>
                        <SelectInput
                            name='vehicle-full-coverage-status'
                            loadingText={t('loading')}
                            value={gapClaim.fullCoverage}
                            options={YesNoOptions}
                            onChange={onChangeFullCoverage}
                            forceDefault
                            defaultIfOneOption
                            addDefaultOption
                            defaultText={t('select_default')}
                        />
                        {gapClaim.fullCoverage == SelectOptionsYesNo.No && (
                            <div
                                className='alert alert-warning panel-selection-alert-container'
                                role='alert'>
                                {t('vehicle_appraisal_needed')}
                            </div>
                        )}
                    </FormInput>
                    {gapClaim.fullCoverage == SelectOptionsYesNo.Yes && (
                        <FormInput
                            id='claim-settlement-received-forminput'
                            label={t('vehicle_settlement_received_question')}
                            errorText={formErrors?.settlementReceived}>
                            <SelectInput
                                name='claim-settlement-received-status'
                                loadingText={t('loading')}
                                value={gapClaim.settlementReceived}
                                options={YesNoOptions}
                                onChange={onChangeSettlementReceived}
                                forceDefault
                                defaultIfOneOption
                                addDefaultOption
                                defaultText={t('select_default')}
                            />
                            {gapClaim.settlementReceived ==
                                SelectOptionsYesNo.No && (
                                <div
                                    className='alert alert-warning panel-selection-alert-container'
                                    role='alert'>
                                    {t('vehicle_settlement_received_message')}
                                </div>
                            )}
                        </FormInput>
                    )}
                    {gapClaim.settlementReceived == SelectOptionsYesNo.Yes && (
                        <FormInput
                            id='settlement-received-date-forminput'
                            label={t('settlement_received_date')}
                            errorText={formErrors?.settlementReceivedDate}>
                            <DateInput
                                name='settlementReceivedDatePicker'
                                useJson
                                forceTimeReset
                                value={gapClaim.settlementReceivedDate}
                                onDateChanged={(dateString) =>
                                    onChangSettlementReceivedDate(dateString)
                                }
                                maximumDate={maxDate()}
                            />
                        </FormInput>
                    )}
                </div>
                <div className='card-footer page-content-footer'>
                    {formErrors && (
                        <ErrorSummary
                            title={t('gap_claim_error_heading')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    disabled={!enableContinueButton()}
                    onClick={onContinueClaim}>
                    {t('continue')}
                </button>
                <button
                    aria-label='cancel-button'
                    name='btnCancel'
                    className='btn btn-danger progress-button'
                    onClick={onCancelClicked}>
                    {t('cancel')}
                </button>
            </div>
        </div>
    );
};

export default ClaimDetailsPage;
